// src/components/learning/placement/TestPlacement.js
// Test de placement — structure générique pour tous les niveaux
// Actuellement implémenté pour le niveau Abjad

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QUESTIONS_ABJAD, { calculerNiveau } from './questions_abjad';

const API = process.env.REACT_APP_API_URL;

const C = {
  green: '#065f46', greenLight: '#E1F5EE', greenMid: '#1D9E75',
  greenBorder: '#a7f3d0', text: '#1a1a1a', muted: '#6b7280',
  white: '#fff', red: '#E24B4A', redLight: '#FCEBEB',
  yellow: '#f59e0b', yellowLight: '#fffbeb',
};

// Sélectionner les questions selon le niveau
const getQuestions = (niveau) => {
  switch (niveau) {
    case 'Abjad': return QUESTIONS_ABJAD;
    default: return QUESTIONS_ABJAD;
  }
};

export default function TestPlacement({ token, niveau = 'Abjad', onClose, onCompleted }) {
  const questions = getQuestions(niveau);
  const TOTAL = questions.length;

  const [step, setStep] = useState('intro'); // intro | question | result
  const [currentQ, setCurrentQ] = useState(0);
  const [reponses, setReponses] = useState({});
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [resultat, setResultat] = useState(null);
  const [saving, setSaving] = useState(false);

  const question = questions[currentQ];

  const handleSelect = (optionId) => {
    if (showFeedback) return;
    setSelected(optionId);
    const correct = question.options.find(o => o.id === optionId)?.correct;
    setShowFeedback(true);
    if (correct) setScore(s => s + 1);
    setReponses(prev => ({ ...prev, [question.id]: { optionId, correct } }));

    setTimeout(() => {
      if (currentQ < TOTAL - 1) {
        setCurrentQ(q => q + 1);
        setSelected(null);
        setShowFeedback(false);
      } else {
        // Fin du test
        const finalScore = score + (correct ? 1 : 0);
        const res = calculerNiveau(finalScore, TOTAL);
        setResultat({ ...res, score: finalScore, total: TOTAL });
        setStep('result');
        saveResult(finalScore, res.niveau);
      }
    }, 1000);
  };

  const saveResult = async (finalScore, niveauAttribue) => {
    if (!token) return;
    setSaving(true);
    try {
      await axios.post(`${API}/progression/test-placement`, {
        niveau_attribue: niveauAttribue,
        score: finalScore,
        reponses,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      console.error('Erreur sauvegarde test:', e);
    } finally {
      setSaving(false);
    }
  };

  const pct = Math.round(((currentQ + 1) / TOTAL) * 100);

  // ── INTRO ──────────────────────────────────────────────────────
  if (step === 'intro') return (
    <div style={overlay}>
      <div style={{ ...box, textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🎯</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 8 }}>
          Test de placement — Niveau {niveau}
        </h2>
        <p style={{ fontSize: 14, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>
          {TOTAL} questions · ~5 minutes<br/>
          Ce test aide ton prof à préparer tes cours
        </p>
        <div style={{
          background: C.greenLight, border: `1px solid ${C.greenBorder}`,
          borderRadius: 12, padding: '16px 20px', marginBottom: 28, textAlign: 'left',
        }}>
          {[
            `🔤 Reconnaître les lettres arabes`,
            `📍 Trouver une lettre dans un mot`,
            `✂️ Compter les syllabes`,
          ].map((item, i) => (
            <div key={i} style={{ fontSize: 13, color: C.text, marginBottom: 8, display: 'flex', gap: 8 }}>
              {item}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', borderRadius: 10,
            border: '1px solid #e5e7eb', background: '#f9f9f9',
            color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Plus tard</button>
          <button onClick={() => setStep('question')} style={{
            flex: 2, padding: '12px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${C.green}, #047857)`,
            color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>Commencer →</button>
        </div>
      </div>
    </div>
  );

  // ── RÉSULTAT ───────────────────────────────────────────────────
  if (step === 'result' && resultat) return (
    <div style={overlay}>
      <div style={{ ...box, textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>{resultat.emoji}</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 8 }}>
          Test terminé !
        </h2>
        <div style={{
          fontSize: 48, fontWeight: 700, color: C.green, marginBottom: 4,
        }}>
          {resultat.score}/{resultat.total}
        </div>
        <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>
          {Math.round((resultat.score / resultat.total) * 100)}% de réussite
        </div>
        <div style={{
          background: C.greenLight, border: `1px solid ${C.greenBorder}`,
          borderRadius: 12, padding: '16px 20px', marginBottom: 24,
        }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 6 }}>
            Niveau suggéré
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.green }}>
            {resultat.emoji} {resultat.niveau}
          </div>
          <div style={{ fontSize: 13, color: C.text, marginTop: 8, lineHeight: 1.5 }}>
            {resultat.message}
          </div>
        </div>
        <p style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
          Ton prof verra ce résultat et adaptera ses cours
        </p>
        <button onClick={() => { if (onCompleted) onCompleted(resultat); onClose(); }} style={{
          width: '100%', padding: '12px', borderRadius: 10, border: 'none',
          background: `linear-gradient(135deg, ${C.green}, #047857)`,
          color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}>
          {saving ? '⏳ Sauvegarde...' : 'Voir ma progression →'}
        </button>
      </div>
    </div>
  );

  // ── QUESTION ───────────────────────────────────────────────────
  return (
    <div style={overlay}>
      <div style={box}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.green}, #047857)`,
          borderRadius: '16px 16px 0 0', padding: '16px 20px',
          color: '#fff',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, opacity: 0.8 }}>
              Question {currentQ + 1} / {TOTAL}
            </span>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none',
              color: '#fff', borderRadius: 6, padding: '3px 8px',
              fontSize: 11, cursor: 'pointer',
            }}>Quitter</button>
          </div>
          {/* Barre progression */}
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 20, height: 5 }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: '#fff', borderRadius: 20,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>

        <div style={{ padding: 24 }}>

          {/* Énoncé */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <p style={{ fontSize: 14, color: C.muted, marginBottom: 12 }}>
              {question.enonce}
            </p>

            {/* Lettre affichée pour reconnaissance */}
            {question.lettre_affichee && (
              <div style={{
                fontSize: 96, color: C.green,
                fontFamily: "'Amiri', serif", direction: 'rtl',
                lineHeight: 1, marginBottom: 8,
                filter: `drop-shadow(0 4px 12px ${C.green}33)`,
              }}>
                {question.lettre_affichee}
              </div>
            )}

            {/* Mot pour syllabes */}
            {question.type === 'compter_syllabes' && (
              <div style={{ marginBottom: 8 }}>
                <div style={{
                  fontSize: 48, color: C.green,
                  fontFamily: "'Amiri', serif", direction: 'rtl',
                  lineHeight: 1, marginBottom: 6,
                }}>
                  {question.mot}
                </div>
                <div style={{ fontSize: 13, color: C.muted }}>
                  ({question.mot_fr})
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: question.type === 'trouver_lettre' ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
            gap: 10,
          }}>
            {question.options.map(opt => {
              let bg = C.white;
              let border = '1px solid #e5e7eb';
              let color = C.text;

              if (showFeedback) {
                if (opt.correct) {
                  bg = C.greenLight; border = `2px solid ${C.green}`; color = C.green;
                } else if (selected === opt.id && !opt.correct) {
                  bg = C.redLight; border = `2px solid ${C.red}`; color = C.red;
                }
              } else if (selected === opt.id) {
                bg = C.greenLight; border = `2px solid ${C.greenMid}`;
              }

              return (
                <div key={opt.id} onClick={() => handleSelect(opt.id)} style={{
                  background: bg, border, borderRadius: 12,
                  padding: '14px 10px', textAlign: 'center',
                  cursor: showFeedback ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                }}>
                  {/* Lettre pour trouver_lettre */}
                  {question.type === 'trouver_lettre' && (
                    <div style={{
                      fontSize: 40, color, fontFamily: "'Amiri', serif",
                      direction: 'rtl', lineHeight: 1, marginBottom: 4,
                    }}>{opt.ar}</div>
                  )}

                  {/* Nom de lettre pour reconnaitre */}
                  {question.type === 'reconnaitre_lettre' && (
                    <>
                      <div style={{ fontSize: 16, fontWeight: 700, color, marginBottom: 2, direction: 'rtl', fontFamily: "'Amiri', serif" }}>
                        {opt.ar}
                      </div>
                      <div style={{ fontSize: 12, color: C.muted }}>{opt.fr}</div>
                    </>
                  )}

                  {/* Mot pour localiser_son */}
                  {question.type === 'localiser_son' && (
                    <>
                      <div style={{ fontSize: 28, marginBottom: 4 }}>{opt.emoji}</div>
                      <div style={{ fontSize: 18, color, fontFamily: "'Amiri', serif", direction: 'rtl', marginBottom: 2 }}>
                        {opt.ar}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>{opt.fr}</div>
                    </>
                  )}

                  {/* Chiffre pour compter_syllabes */}
                  {question.type === 'compter_syllabes' && (
                    <div style={{ fontSize: 28, fontWeight: 700, color }}>
                      {opt.val}
                    </div>
                  )}

                  {/* Feedback emoji */}
                  {showFeedback && opt.correct && (
                    <div style={{ fontSize: 16, marginTop: 4 }}>✅</div>
                  )}
                  {showFeedback && selected === opt.id && !opt.correct && (
                    <div style={{ fontSize: 16, marginTop: 4 }}>❌</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Score en cours */}
          <div style={{
            textAlign: 'center', marginTop: 16,
            fontSize: 12, color: C.muted,
          }}>
            Score : {score} / {currentQ + (showFeedback ? 1 : 0)}
          </div>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.6)', zIndex: 99999,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};

const box = {
  background: '#fff', borderRadius: 16,
  width: '100%', maxWidth: 520, maxHeight: '95vh',
  overflowY: 'auto',
  boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
};
